import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/authStore';
import { useTheme } from '../../../context/ThemeContext';
import { HexLockup } from '../../../components/FieldCertLogo';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { EyeIcon, Spinner } from '../../../components/ui/auth/AuthIcons';
import api from '../../../lib/api';
import toast from 'react-hot-toast';

const REMEMBER_KEY = 'fieldcert_login_remember';
const EMAIL_KEY = 'fieldcert_login_email';

const BRAND_FEATURES = [
  'Multi-trade asset management',
  'AS/NZS compliance reports',
  'Works offline in the field',
  'Multi-site & multi-team',
];

function CheckIcon() {
  return (
    <svg className="login-success-icon" width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true">
      <circle cx="28" cy="28" r="26" fill="rgba(45,184,90,0.15)" stroke="#2db85a" strokeWidth="2" /> {/* check-rules:ignore — success-icon stroke; SVG-local brand color */}
      <polyline points="16,28 23,36 40,19" stroke="#2db85a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" /> {/* check-rules:ignore — success-icon stroke; SVG-local brand color */}
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" />
      <line x1="7" y1="4" x2="7" y2="7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="7" cy="10" r="0.75" fill="currentColor" />
    </svg>
  );
}

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [shakeBtn, setShakeBtn] = useState(false);
  const [connectionOk, setConnectionOk] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const { setUser } = useAuthStore();
  const { effectiveTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const stored = localStorage.getItem(REMEMBER_KEY) === '1';
      const storedEmail = localStorage.getItem(EMAIL_KEY) || '';
      if (stored && storedEmail) {
        setRememberMe(true);
        setEmail(storedEmail);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    api
      .get('/health')
      .then(() => setConnectionOk(true))
      .catch((err) => {
        setConnectionOk(false);
        console.error('[Login] Health check failed:', {
          code: err.code,
          message: err.message,
          baseURL: api.defaults.baseURL,
          origin: typeof window !== 'undefined' ? window.location.origin : 'ssr',
        });
      });
  }, []);

  const validate = () => {
    const errs = {};
    if (!email.trim()) errs.email = 'Email required';
    else if (!email.includes('@')) errs.email = 'Invalid email address';
    if (!password) errs.password = 'Password required';
    else if (password.length < 6) errs.password = 'Too short — min 6 characters';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setShakeBtn(true);
      setTimeout(() => setShakeBtn(false), 500);
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });

      try {
        if (rememberMe) {
          localStorage.setItem(REMEMBER_KEY, '1');
          localStorage.setItem(EMAIL_KEY, email.trim());
        } else {
          localStorage.removeItem(REMEMBER_KEY);
          localStorage.removeItem(EMAIL_KEY);
        }
      } catch { /* ignore */ }

      setUser(data.user);

      if (data.must_change_password || data.user?.mustChangePassword) {
        navigate('/force-password-change', { replace: true });
        return;
      }

      setShowSuccess(true);
      setTimeout(() => navigate('/', { replace: true }), 1600);
    } catch (err) {
      const isNetworkError = err.code === 'ERR_NETWORK' || !err.response;
      if (isNetworkError) {
        setErrors((prev) => ({
          ...prev,
          network: 'Unable to connect — check your signal',
        }));
      } else {
        // Server rejected the login (401 etc.) — surface it on the password
        // field with the same red/shake treatment as client-side validation.
        setErrors((prev) => ({
          ...prev,
          password: err.response?.data?.error || 'Incorrect email or password',
        }));
        setShakeBtn(true);
        setTimeout(() => setShakeBtn(false), 500);
      }
      toast.error(
        isNetworkError
          ? 'Cannot connect to server. Check backend is running (port 3000) and reachable from this device.'
          : (err.response?.data?.error || 'Login failed')
      );
    } finally {
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="login-success">
        <CheckIcon />
        <div className="login-success-title">Signed in</div>
        <div className="login-success-detail">
          Welcome back.<br />
          <span style={{ color: 'var(--text-secondary)' }}>{email}</span>
        </div>
        <div className="login-success-redirect">Redirecting to dashboard…</div>
      </div>
    );
  }

  const ctaClass = `login-cta${shakeBtn ? ' login-cta--shake' : ''}`;

  return (
    <div className="login-shell">
      <div className="login-shell-bg" aria-hidden="true" />

      {/* Desktop brand panel */}
      <aside className="login-brand-panel" aria-label="FieldCert overview">
        <div className="login-brand-panel-inner">
          <div className="login-brand-panel-top">
            <HexLockup iconSize={38} wmSize="sm" gap={12} surface="dark" />
          </div>
          <p className="login-brand-tagline">
            Programmed asset compliance for Australian contractors.
          </p>
          <ul className="login-brand-features">
            {BRAND_FEATURES.map((text) => (
              <li key={text}>
                <span className="login-brand-feature-icon" aria-hidden="true">✓</span>
                {text}
              </li>
            ))}
          </ul>
          <p className="login-brand-copyright">© {new Date().getFullYear()} FieldCert Pty Ltd</p>
        </div>
      </aside>

      {/* Auth panel */}
      <div className="login-auth-panel">
        <div className="login-auth-inner">
          {/* Mobile lockup */}
          <div className="login-auth-brand" style={{ marginBottom: '1.5rem' }}>
            <HexLockup iconSize={48} wmSize="sm" gap={12} light={effectiveTheme === 'light'} />
          </div>

          {/* Heading */}
          <div className="login-heading">
            <h1 className="login-heading-title">Sign in</h1>
            <p className="login-heading-sub">Get to work. We&rsquo;ll handle the paperwork.</p>
          </div>

          <hr className="login-gold-rule" />

          {/* Form */}
          <form onSubmit={handleSubmit} className="login-form" noValidate>
            {/* Email */}
            <Input
              label={errors.email || 'Email'}
              error={!!errors.email}
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((prev) => ({ ...prev, email: null }));
              }}
              required
              autoComplete="email"
              placeholder="technician@fieldcert.com.au"
              disabled={loading}
            />

            {/* Password */}
            <Input
              label={errors.password || 'Password'}
              error={!!errors.password}
              id="password"
              type={showPassword ? 'text' : 'password'}
              endAdornment={(
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <EyeIcon open={showPassword} />
                </button>
              )}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors((prev) => ({ ...prev, password: null }));
              }}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              disabled={loading}
            />

            {/* Remember + Forgot */}
            <div className="login-options-row">
              <button
                type="button"
                className="login-remember"
                onClick={() => setRememberMe((v) => !v)}
                aria-pressed={rememberMe}
              >
                <span className={`login-remember-box${rememberMe ? ' login-remember-box--checked' : ''}`}>
                  {rememberMe && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
                      <polyline points="1,4 3.5,7 9,1" stroke="var(--brand-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span>Remember me</span>
              </button>
              <Link className="login-forgot-link" to="/forgot-password">
                Forgot?
              </Link>
            </div>

            {/* Connection / network error */}
            {(connectionOk === false || errors.network) && (
              <div className="login-alert" role="alert" style={{ color: 'var(--danger)' }}>
                <AlertIcon />
                <span>{errors.network || 'Unable to connect — check your signal'}</span>
              </div>
            )}

            {/* CTA */}
            <Button type="submit" variant="primary" className={ctaClass} disabled={loading}>
              {loading ? <><Spinner /> Signing in…</> : 'Sign In'}
            </Button>
          </form>

          <div className="login-footer">Secured · FieldCert Platform</div>
        </div>
      </div>
    </div>
  );
}

export default Login;
